// ============================================================
// TMR Portal Admin — reading an audit snapshot as a change, not as a blob
// ============================================================
import type { AuditSnapshot } from './contract';

/** One field that differs between the two snapshots of an audit entry. */
export interface AuditFieldChange {
  /** Property name as the backend recorded it; label it with `auditFieldLabel`. */
  field: string;
  /** The value before the write; undefined when the entity did not exist yet. */
  before: unknown;
  /** The value after the write; undefined when the entity was deleted. */
  after: unknown;
}

/**
 * The fields that actually changed between two snapshots of an audited entity.
 *
 * The audit log stores whole snapshots, so an `updated` entry of a Role is a dozen identical values
 * around the one that moved. Diffing turns it into the sentence an operator is looking for — "o papel
 * pai passou de X para Y" — while the full JSON stays available for the rare case where it matters.
 *
 * Values are compared by their JSON serialization, which is exactly how the backend wrote them: it makes
 * nested objects and arrays compare by content rather than by identity.
 *
 * @param before The snapshot before the write; null for a creation.
 * @param after The snapshot after the write; null for a deletion.
 * @returns The differing fields, in the order the snapshots list them.
 */
export function changedFields(
  before: AuditSnapshot | null,
  after: AuditSnapshot | null,
): AuditFieldChange[] {
  const fields = [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])];

  return fields
    .map((field) => ({
      field,
      before: before ? before[field] : undefined,
      after: after ? after[field] : undefined,
    }))
    .filter((change) => !isSameValue(change.before, change.after));
}

/**
 * Whether two snapshot values are the same, by content — `JSON.stringify` is how they were written.
 *
 * An absent value is never the same as a null one, which is the whole reason this is not a plain
 * `stringify(a ?? null)` comparison. Collapsing them dropped, from every creation diff, each field the
 * entity was created with as null — and on a `UserRoleAssignment` those fields are `CompanyId`,
 * `HoldingId` and `ExpiresAt`, so the most consequential grant there is, unscoped and permanent, read on
 * screen exactly like a narrow one. {@link formatAuditValue} has always distinguished the two; this is
 * what lets the field reach it.
 */
function isSameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * A snapshot value as a short string for a table cell.
 *
 * An absent value and a null one are not the same thing and must not read the same: a field the
 * creation never had is "—", while one the backend explicitly set to null is "vazio".
 *
 * @param value The raw value from the snapshot.
 */
export function formatAuditValue(value: unknown): string {
  if (value === undefined) return '—';
  if (value === null) return 'vazio';
  if (typeof value === 'boolean') return value ? 'sim' : 'não';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return JSON.stringify(value);
}
